import { computed, effect, input, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UiIconLibrary } from './icons-library';
import { htmlEscape } from '@core/shared/utils';
import * as i0 from "@angular/core";
import * as i1 from "./icons-library";
import * as i2 from "@angular/platform-browser";
/**
 * a custom lightweight implementation of fa-icon component.
 * https://github.com/FortAwesome/react-fontawesome/issues/232#issuecomment-115
 * https://github.com/FortAwesome/angular-fontawesome/blob/main/src/lib/icon/icon.component.ts
 */
export let UiIconComponent = /*#__PURE__*/(() => {
  class UiIconComponent {
    constructor(iconLib, sanitizer, cd) {
      this.iconLib = iconLib;
      this.sanitizer = sanitizer;
      this.cd = cd;
      this.isImportant = input(true, ...(ngDevMode ? [{
        debugName: "isImportant"
      }] : /* istanbul ignore next */[]));
      this.iconInput = input.required({
        ...(ngDevMode ? {
          debugName: "iconInput"
        } : /* istanbul ignore next */{}),
        alias: 'icon'
      });
      this.iconIsMissing = computed(() => {
        const iconInput = this.iconInput();
        if (!iconInput) return true;
        if (typeof iconInput === 'string' && !this.iconLib.getIconDefinition('fas', iconInput)) return true;
        return false;
      }, ...(ngDevMode ? [{
        debugName: "iconIsMissing"
      }] : /* istanbul ignore next */[]));
      this.iconDef = computed(() => {
        const iconInput = this.iconInput();
        if (this.iconIsMissing()) return this.iconLib.getIconDefinition('fas', 'circle-xmark');
        if (Array.isArray(iconInput)) return this.iconLib.getIconDefinition(iconInput[0], iconInput[1]);
        if (typeof iconInput === 'object') return iconInput;
        if (typeof iconInput === 'string') return this.iconLib.getIconDefinition('fas', iconInput);
      }, ...(ngDevMode ? [{
        debugName: "iconDef"
      }] : /* istanbul ignore next */[]));
      this.size = input("1x", ...(ngDevMode ? [{
        debugName: "size"
      }] : /* istanbul ignore next */[]));
      this.color = input(undefined, ...(ngDevMode ? [{
        debugName: "color"
      }] : /* istanbul ignore next */[]));
      this.fixedWidth = input(false, ...(ngDevMode ? [{
        debugName: "fixedWidth"
      }] : /* istanbul ignore next */[]));
      this.spin = input(false, ...(ngDevMode ? [{
        debugName: "spin"
      }] : /* istanbul ignore next */[]));
      this.classList = computed(() => {
        let list = 'svg-inline--fa';
        if (this.size()) list += ` fa-${this.size()}`;
        if (this.color()) list += ` color-${this.color()}`;
        if (this.fixedWidth()) list += ` fa-fw`;
        if (this.spin()) list += ` fa-spin`;
        return list;
      }, ...(ngDevMode ? [{
        debugName: "classList"
      }] : /* istanbul ignore next */[]));
      this.renderedIconHTML = computed(() => {
        const iconDef = this.iconDef();
        if (!iconDef) return '';
        /**
        * fa-icon calls icon() from svg-core which is handled here:
        * https://github.com/FortAwesome/Font-Awesome/blob/6.x/js-packages/%40fortawesome/fontawesome-svg-core/index.js#L2170
        * which is calling makeInlineSvgAbstract. It builds a data representation of a SVG element which is then created using toHtml
        */
        const attributes = {
          class: this.classList(),
          viewBox: '0 0 ' + iconDef.icon[0] + ' ' + iconDef.icon[1],
          role: 'img',
          xmlns: "http://www.w3.org/2000/svg"
        };
        const inner = this.makeSvg(iconDef);
        const attributesString = Object.entries(attributes).reduce((str, [name, value]) => {
          return str + `${name}="${htmlEscape(value)}" `;
        }, '');
        const html = `<svg ${attributesString}>${inner}</svg>`;
        return this.sanitizer.bypassSecurityTrustHtml(html);
      }, ...(ngDevMode ? [{
        debugName: "renderedIconHTML"
      }] : /* istanbul ignore next */[]));
      this.renderedIconHTMLUpdate = effect(() => {
        this.innerHTMLBinding = this.renderedIconHTML();
        this.cd.markForCheck();
      }, ...(ngDevMode ? [{
        debugName: "renderedIconHTMLUpdate"
      }] : /* istanbul ignore next */[]));
    }
    makeSvg(def) {
      const {
        icon
      } = def;
      if (!Array.isArray(icon)) {
        console.log(def);
        throw new Error('invalid icon definition!');
      }
      // const [width, height, ligatures, unicode, svgPathData] = icon;
      const svgPathData = icon[4];
      const content = Array.isArray(svgPathData) ? `<g>${svgPathData.map(x => `<path d="${x}" />`)}</g>` : `<path fill="currentColor" d="${svgPathData}" />`;
      return content;
    }
    static {
      this.ɵfac = function UiIconComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UiIconComponent)(i0.ɵɵdirectiveInject(i1.UiIconLibrary), i0.ɵɵdirectiveInject(i2.DomSanitizer), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: UiIconComponent,
        selectors: [["ui-icon"]],
        hostVars: 6,
        hostBindings: function UiIconComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵdomProperty("innerHTML", ctx.innerHTMLBinding, i0.ɵɵsanitizeHtml);
            i0.ɵɵattribute("title", ctx.iconIsMissing() ? "Icon not found: " + ctx.iconInput() : undefined);
            i0.ɵɵclassProp("important", ctx.isImportant())("icon-missing", ctx.iconIsMissing());
          }
        },
        inputs: {
          isImportant: [1, "isImportant"],
          iconInput: [1, "icon", "iconInput"],
          size: [1, "size"],
          color: [1, "color"],
          fixedWidth: [1, "fixedWidth"],
          spin: [1, "spin"]
        },
        decls: 0,
        vars: 0,
        template: function UiIconComponent_Template(rf, ctx) {},
        styles: ["ui-icon.icon-missing{color:#fff!important;background:#e30a0a!important;display:inline-block}ui-icon>svg:not(:root).svg-inline--fa,ui-icon>svg:not(:host).svg-inline--fa{overflow:visible;box-sizing:content-box}ui-icon>.svg-inline--fa.fa-fw{width:var(--fa-fw-width, 1.25em)}ui-icon>.fa-fw{text-align:center;width:1.25em}ui-icon>.svg-inline--fa{display:var(--fa-display, inline-block);height:1em;overflow:visible;vertical-align:-.125em}ui-icon>.fa-xs{font-size:.75em;line-height:.08333em;vertical-align:.125em}ui-icon>.fa-sm{font-size:.875em;line-height:.07143em;vertical-align:.05357em}ui-icon>.fa-lg{font-size:1.25em;line-height:.05em;vertical-align:-.075em}ui-icon>.fa-xl{font-size:1.5em;line-height:.04167em;vertical-align:-.125em}ui-icon>.fa-2x{font-size:2em}ui-icon>.fa-3x{font-size:3em}ui-icon>.fa-4x{font-size:4em}ui-icon>.svg-inline--fa.fa-xs{vertical-align:0em}ui-icon>.svg-inline--fa.fa-sm{vertical-align:-.07143em}ui-icon>.svg-inline--fa.fa-lg{vertical-align:-.2em}ui-icon>.svg-inline--fa.fa-xl{vertical-align:-.25em}ui-icon>.svg-inline--fa.fa-2xl{vertical-align:-.3125em}@keyframes fa-spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}ui-icon>.fa-spin{animation-name:fa-spin;animation-delay:var(--fa-animation-delay, 0s);animation-direction:var(--fa-animation-direction, normal);animation-duration:var(--fa-animation-duration, 2s);animation-iteration-count:var(--fa-animation-iteration-count, infinite);animation-timing-function:var(--fa-animation-timing, linear)}body.a11y-no-distraction-mode ui-icon:not(.important){display:none}\n\n/*# sourceMappingURL=icon.component.ts-angular-inline--2.css.map*/"],
        encapsulation: 2
      });
    }
  }
  return UiIconComponent;
})();